import React from 'react';
import { CalendarDaysIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { Container, Section } from '../ui/Section';
import { ScriptEyebrow } from '../ui/ScriptEyebrow';
import { ButtonLink } from '../ui/Button';
import { PhotoFrame } from '../ui/PhotoFrame';
import { Reveal, stagger } from '../motion/Reveal';
import { FloatingPhoto } from '../motion/FloatingPhoto';
import { DoodleSquiggle } from '../brand/Doodles';
import { findVisibleRecurring, typicalWeekCards } from '../../data/recurring';
import { scheduleDisclaimer } from '../../data/site';
import { typicalWeekPhoto } from '../../data/photos';
const labelAccents: Record<string, string> = {
  'weekly-worship': 'bg-brand-yellow',
  'community-groups': 'bg-brand-blue',
  recreation: 'bg-brand-pink'
};

/**
 * Permanent, low-maintenance overview of a normal BCM week. Confirmed schedules
 * come from data/recurring.ts; cards without a confirmed record show a neutral
 * status line instead of any tentative day, time, or location.
 */
export function TypicalWeek({
  showPhoto = true
}: {
  showPhoto?: boolean;
}) {
  return <Section className="relative overflow-hidden bg-[#fffdf6] py-16 sm:py-20" labelledBy="typical-week-heading" data-id="element-409" data-mp="2cezzzrf71fiy">
      <Container data-id="element-410" data-mp="2h6ztb10rda6k">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_0.5fr] lg:gap-14" data-id="element-411" data-mp="2h6hz09xnp61a">
          <Reveal data-id="element-412" data-mp="2m3q8lm4qo1qx">
            <ScriptEyebrow accent="var(--bcm-light-blue)" data-id="element-413" data-mp="2m49oyyeqqi2u">Find your rhythm</ScriptEyebrow>
            <h2 id="typical-week-heading" className="mt-3 font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-[2.6rem]" data-id="element-414" data-mp="1pgd8run7tos5">
              A typical week at BCM
            </h2>
            <p className="mt-4 max-w-2xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-415" data-mp="23ejwwj2d4aem">
              There is more than one way to get involved with UCF BCM. Weekly activities can change
              by semester, so check our official channels for the latest times, locations, and
              special events.
            </p>
          </Reveal>

          {showPhoto ? <Reveal x={16} delay={0.12} className="relative hidden lg:block" data-id="element-416" data-mp="2p3qgxk8yaqh7">
              <DoodleSquiggle className="pointer-events-none absolute -top-8 right-2 h-7 w-28" color="var(--bcm-dark-blue)" data-id="element-417" data-mp="2k5oxuzbiyr5q" />
              <FloatingPhoto range={5} duration={8.5} delay={0.5} data-id="element-418" data-mp="2u4ee6nnrw3jv">
                <PhotoFrame photo={typicalWeekPhoto} ratio="aspect-[4/5]" rotate="rotate-2" className="transition-transform duration-300 hover:rotate-0" data-id="element-419" data-mp="26f39iv3axp0g" />
              </FloatingPhoto>
            </Reveal> : null}
        </div>

        <ul className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-420" data-mp="2bdmwmytmt6ue">
          {typicalWeekCards.map((card, index) => {
          const recurring = findVisibleRecurring(card.recurringId);
          return <Reveal key={card.id} as="li" delay={stagger(index)} className="flex h-full flex-col rounded-[20px] border-2 border-brand-navy bg-white p-6 transition-transform duration-200 hover:-translate-y-1" data-id="element-421" data-mp="1iqamjo4nqrhz" data-mpi={index}>
                <span className={`w-fit rounded-full px-3 py-1 font-meta text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-navy ${labelAccents[card.id] ?? 'bg-brand-blue'}`} data-id="element-422" data-mp="3kf6pslihbc9w">
                  {card.label}
                </span>
                <h3 className="mt-4 font-display text-xl font-bold leading-snug text-brand-navy sm:text-2xl" data-id="element-423" data-mp="lvzskr0tsjsr">
                  {card.title}
                </h3>

                {recurring ? <dl className="mt-4 space-y-2 font-meta text-[13px] text-ink" data-id="element-424" data-mp="by3hfz7w1oun">
                    <div className="flex items-start gap-2" data-id="element-425" data-mp="2smzappimg7xm">
                      <dt className="sr-only" data-id="element-426" data-mp="2splinq9fcpgd">Schedule</dt>
                      <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-427" data-mp="2wq5b4gj1z4w6" />
                      <dd className="font-semibold text-brand-navy" data-id="element-428" data-mp="3850aeoj8kjeq">
                        {recurring.day}s at {recurring.time} during the school year
                      </dd>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-429" data-mp="2y7ug77fbscaa">
                      <dt className="sr-only" data-id="element-430" data-mp="2y5hr8ab5h1nq">Location</dt>
                      <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-431" data-mp="3vtrvyx2bm3ij" />
                      <dd data-id="element-432" data-mp="3lwdo3lugqn1u">{recurring.locationDetail ?? recurring.location}</dd>
                    </div>
                  </dl> : null}

                {card.copy ? <p className="mt-4 font-sans text-[15px] leading-relaxed text-ink" data-id="element-433" data-mp="2ahi3zkd1rtdw">{card.copy}</p> : null}

                {card.scheduleStatus ? <p className="mt-4 flex items-center gap-2 font-meta text-[13px] font-semibold text-brand-navy" data-id="element-434" data-mp="26i992c69tpgr">
                    <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" data-id="element-435" data-mp="pzpcsc76lveb" />
                    {card.scheduleStatus}
                  </p> : null}

                {card.clarification ? <p className="mt-3 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-436" data-mp="zp8zxu1xgbez">
                    {card.clarification}
                  </p> : null}

                {card.cta ? <div className="mt-6 pt-1" data-id="element-437" data-mp="19n6suuuqewdx">
                    <ButtonLink to={card.cta.to} variant="secondary" data-id="element-438" data-mp="19lvigc6s6qdu">
                      {card.cta.label}
                    </ButtonLink>
                  </div> : null}
              </Reveal>;
        })}
        </ul>

        <Reveal delay={0.1} data-id="element-439" data-mp="3fh6i6zr1jmx5">
          <p className="mt-6 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-440" data-mp="3fhydsakn1knf">
            {scheduleDisclaimer}
          </p>
        </Reveal>
      </Container>
    </Section>;
}